import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import DataTable from './common/DataTable';
import ConversationModal from './common/ConversationModal';
import Alert from './common/Alert';
import LoadingSpinner from './common/LoadingSpinner';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import SolicitudesDropdown from './common/SolicitudesDropdown';
import UserSessionMenu from './common/UserSessionMenu';
import SubscriptionBlockedModal from './common/SubscriptionBlockedModal';
import AsistenteService from '../services/AsistenteService';
import AdminAsistenteService from '../services/AdminAsistenteService';
import GptClasicoReportsService from '../services/GptClasicoReportsService';
import ThreadsService from '../services/ThreadsService';
import ColegioService from '../services/ColegioService';
import ParticularService from '../services/ParticularService';
import particularesApiService from '../services/ParticularesApiService';
import { hasOpenAIChatsEnabled, isAdminColegio, isFeatureEnabled } from '../utils/adminMenu';
import { ITEMS_PER_PAGE_OPTIONS } from '../utils/pagination';
import './Reports.css';
import AdminMenu from './common/menus/AdminMenu';
import ProfessorMenu from './common/menus/ProfessorMenu';
import ParticularDashboardMenu from './common/menus/ParticularDashboardMenu';
import {
  CALENDARIO_SUBSCRIPTION_BLOCKED_MESSAGE,
  isParticularCalendarioAllowed,
  isParticularCalendarioMenuItem,
  isParticularChatGPTAllowed,
  isParticularChatGPTMenuItem
} from '../utils/particularChatGPTAccess';

const isReportsUser = (currentUser) => {
  if (!currentUser) return false;
  const roleName = (currentUser.roleName || currentUser.nombreRol || currentUser.role || '').toLowerCase();
  return (
    (roleName.includes('particular') && !roleName.includes('alumno')) ||
    roleName === 'profesor' ||
    roleName.includes('profesor') ||
    roleName.includes('profe') ||
    roleName === 'admin' ||
    roleName.includes('adminarea') ||
    roleName.includes('admin area') ||
    roleName.includes('admin')
  );
};

const isAdminUser = (currentUser) => {
  if (!currentUser) return false;
  const roleName = (currentUser.roleName || currentUser.nombreRol || currentUser.role || '').toLowerCase();
  return roleName === 'admin' || roleName.includes('adminarea') || roleName.includes('admin area') || roleName.includes('admin');
};

const isClasicoAssistant = (assistant) => {
  if (!assistant) return false;
  if (assistant.esClasico !== undefined) return Boolean(assistant.esClasico);
  if (assistant.EsClasico !== undefined) return Boolean(assistant.EsClasico);

  return Boolean(
    assistant.gptClasicoModelo || assistant.GPTClasicoModelo ||
    assistant.gptClasicoNombre || assistant.GPTClasicoNombre ||
    assistant.gptClasicoCreacionFinalizada || assistant.GPTClasicoCreacionFinalizada
  );
};

const toBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'si' || normalized === 'sí';
  }
  return Boolean(value);
};

const isClassicAssistantReady = (assistant) => {
  const creationFinalized =
    assistant?.gptClasicoCreacionFinalizada ??
    assistant?.GPTClasicoCreacionFinalizada ??
    assistant?.fechaFinalizacion ??
    assistant?.FechaFinalizacion ??
    false;

  return toBoolean(creationFinalized);
};

const isAssistantPendingForReports = (assistant) => (
  isClasicoAssistant(assistant) && !isClassicAssistantReady(assistant)
);

const DEFAULT_ITEMS_PER_PAGE = 10;
const DEFAULT_MESSAGES_PER_PAGE = 300;
const ARGENTINA_UTC_OFFSET_HOURS = -3;
const ARGENTINA_TIMEZONE = 'America/Argentina/Buenos_Aires';
const EMPTY_MENSAJES_STATS = {
  diaMasActivo: null,
  mensajesDiaMasActivo: 0,
  horaPico: null,
  mensajesHoraPico: 0,
  promedioMensajesPorDiaActivo: 0,
  diasConActividad: 0,
  ultimaActividad: null
};

const getRange = (page, pageSize) => {
  const currentPage = Math.max(page || 1, 1);
  const take = Math.max(pageSize || DEFAULT_ITEMS_PER_PAGE, 1);
  const start = (currentPage - 1) * take;
  return { start, end: start + take - 1 };
};

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultMessagesDateRange = () => {
  const today = new Date();
  return {
    fechaDesde: toDateInput(new Date(today.getFullYear(), today.getMonth(), 1)),
    fechaHasta: toDateInput(today)
  };
};

const normalizeAssistant = (asistente, options = {}) => {
  const fechaCreacion =
    asistente.fechaCreacion ??
    asistente.fechaAsignacion ??
    asistente.fechaAlta ??
    asistente.FechaCreacion ??
    asistente.FechaAsignacion ??
    asistente.FechaAlta ??
    null;

  const esClasico = options.forceClasico ?? (
    Boolean(asistente.gptClasicoModelo || asistente.GPTClasicoModelo) ||
    Boolean(asistente.gptClasicoNombre || asistente.GPTClasicoNombre) ||
    Boolean(asistente.gptClasicoCreacionFinalizada || asistente.GPTClasicoCreacionFinalizada)
  );

  return {
    ...asistente,
    idOpenAIAssistant: asistente.idOpenAIAssistant || asistente.IdOpenAIAssistant,
    nombreAsistente:
      asistente.nombreAsistente ??
      asistente.gptClasicoNombre ??
      asistente.GPTClasicoNombre ??
      asistente.nombre ??
      asistente.Nombre ??
      '',
    descripcion:
      asistente.gptClasicoDescripcion ??
      asistente.GPTClasicoDescripcion ??
      asistente.descripcion ??
      asistente.Descripcion ??
      '',
    estado: isClassicAssistantReady(asistente) ? 'Listo' : 'Pendiente',
    esAsignado: asistente.esAsignado ?? asistente.EsAsignado ?? false,
    fechaAlta: fechaCreacion,
    esClasico
  };
};

const getClasicosSearchableDate = (fecha) => {
  if (!fecha) return '';
  const fechaObj = fecha instanceof Date ? fecha : new Date(fecha);
  if (Number.isNaN(fechaObj.getTime())) {
    return '';
  }

  return fechaObj.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const Reports = () => {
  const { user, logout, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canAccessReports = isReportsUser(user);
  const isAdmin = isAdminUser(user);
  const roleName = (user?.roleName || user?.nombreRol || user?.role || '').toLowerCase();
  const activeParticular = useMemo(() => ParticularService.getActiveParticular(), []);
  const activeParticularId = useMemo(() => ParticularService.getActiveParticularId(), []);
  const [particularContext, setParticularContext] = useState(null);
  const [subscriptionBlockedOpen, setSubscriptionBlockedOpen] = useState(false);
  const [subscriptionBlockedMessage, setSubscriptionBlockedMessage] = useState('');
  const [subscriptionBlockedDismissed, setSubscriptionBlockedDismissed] = useState(false);
  const requestedScope = location.state?.scope || new URLSearchParams(location.search).get('scope');
  const isGeneralAdmin = roleName.includes('admin') && !isAdminColegio(user);
  const isParticularRole = roleName.includes('particular');
  const isExplicitParticularScope = requestedScope === 'particulares' || requestedScope === 'particular';
  const isParticularContext = isExplicitParticularScope ||
    isParticularRole ||
    (isGeneralAdmin && Boolean(activeParticular || activeParticularId));
  const effectiveParticular = particularContext || activeParticular;
  const canUseParticularChatGPTFeatures = !isParticularContext || isParticularChatGPTAllowed(effectiveParticular);
  const canUseParticularCalendarioFeature = !isParticularContext || isParticularCalendarioAllowed(effectiveParticular);
  const hasKnownParticularChatGPTAccess = isParticularContext && isParticularChatGPTAllowed(activeParticular);
  const roleLabel = isAdmin ? (String(user?.roleName || user?.nombreRol || user?.role || 'Admin')) : 'Profesor';
  const sidebarRoleLabel = isParticularContext ? 'Particular' : roleLabel;
  const sidebarUserIcon = isParticularContext ? 'fas fa-user-tie' : 'fas fa-user';
  const hasModuloAlumnos = useMemo(() => {
    if (isParticularContext) return false;
    const flags = ColegioService.getFeatureFlags();
    return isFeatureEnabled(flags, 'ModuloAlumnos');
  }, [isParticularContext]);
  const hasAgentesClasicos = useMemo(() => {
    if (isParticularContext) return canUseParticularChatGPTFeatures;
    const flags = ColegioService.getFeatureFlags();
    return isFeatureEnabled(flags, 'PermiteAgentesClasicosGPT');
  }, [canUseParticularChatGPTFeatures, isParticularContext]);
  const hasOpenAIChats = useMemo(() => {
    if (isParticularContext) return false;
    const flags = ColegioService.getFeatureFlags();
    return hasOpenAIChatsEnabled(flags);
  }, [isParticularContext]);
  
  // Estados
  const [asistentesClasicos, setAsistentesClasicos] = useState([]);
  const [asistentesOpenAI, setAsistentesOpenAI] = useState([]);
  const [isLoadingClasicos, setIsLoadingClasicos] = useState(true);
  const [isLoadingOpenAI, setIsLoadingOpenAI] = useState(true);
  const [clasicosSearch, setClasicosSearch] = useState('');
  const [asistentesClasicosSearchPool, setAsistentesClasicosSearchPool] = useState([]);
  const [clasicosSearchPoolLoaded, setClasicosSearchPoolLoaded] = useState(false);
  const [isLoadingClasicosSearchPool, setIsLoadingClasicosSearchPool] = useState(false);
  const [paginacionClasicos, setPaginacionClasicos] = useState({
    paginaActual: 1,
    tamañoPagina: DEFAULT_ITEMS_PER_PAGE,
    total: 0
  });
  const [paginacionOpenAI, setPaginacionOpenAI] = useState({
    paginaActual: 1,
    tamañoPagina: DEFAULT_ITEMS_PER_PAGE,
    total: 0
  });
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [showThreadsView, setShowThreadsView] = useState(false);
  
  // Estados para mensajes
  const [mensajes, setMensajes] = useState([]);
  const [isLoadingMensajes, setIsLoadingMensajes] = useState(false);
  const [mensajesSearch, setMensajesSearch] = useState('');
  const [mensajesDateRange, setMensajesDateRange] = useState(getDefaultMessagesDateRange);
  const [informacionUtil, setInformacionUtil] = useState({
    texto: '',
    error: '',
    mensajesAnalizados: 0
  });
  const [paginacionMensajes, setPaginacionMensajes] = useState({
    paginaActual: 1,
    tamañoPagina: DEFAULT_MESSAGES_PER_PAGE,
    total: 0,
    totalPaginas: 0
  });
  const [estadisticasMensajes, setEstadisticasMensajes] = useState(EMPTY_MENSAJES_STATS);

  // Estados paral modal de conversación
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [conversationSource, setConversationSource] = useState('db');
  const [particularContextReady, setParticularContextReady] = useState(false);
  const particularAccessReady = particularContextReady || hasKnownParticularChatGPTAccess;
  const isParticularContextPending = isParticularContext && !particularAccessReady && !alert.show;
  const isParticularSubscriptionBlocked = isParticularContext && particularAccessReady && !canUseParticularChatGPTFeatures;
  const subscriptionBlockedModalOpen = subscriptionBlockedOpen || (isParticularSubscriptionBlocked && !subscriptionBlockedDismissed);
  const isLoading = isParticularContextPending || isLoadingClasicos || (!isParticularContext && isLoadingOpenAI);
  const normalizedClasicosSearch = useMemo(() => clasicosSearch.trim().toLowerCase(), [clasicosSearch]);
  const isClasicosSearchActive = normalizedClasicosSearch.length > 0;

  const handleLogout = () => {
    logout();
  };

  const handleChangeParticular = () => {
    ParticularService.setActiveParticular(null);
    navigate('/admin/select-colegio', { replace: true, state: { scope: 'particulares' } });
  };

  const closeSubscriptionBlockedModal = () => {
    setSubscriptionBlockedOpen(false);
    setSubscriptionBlockedMessage('');
    setSubscriptionBlockedDismissed(true);
  };

  useEffect(() => {
    if (!isParticularContext) {
      setParticularContextReady(true);
      setParticularContext(null);
      return;
    }

    if (!activeParticularId && isGeneralAdmin) {
      setParticularContextReady(false);
      setParticularContext(null);
      setIsLoadingClasicos(false);
      setIsLoadingOpenAI(false);
      setAlert({
        show: true,
        message: 'Debe seleccionar un particular para consultar reportes.',
        type: 'error'
      });
      return;
    }

    let cancelled = false;
    setParticularContextReady(false);

    particularesApiService.getContexto()
      .then((contextResponse) => {
        if (cancelled) return;
        const particular = contextResponse?.particular || contextResponse?.Particular || activeParticular || null;
        if (particular) {
          ParticularService.setActiveParticular(particular);
        }
        setParticularContext(particular);
        setParticularContextReady(true);
      })
      .catch((error) => {
        if (cancelled) return;
        setParticularContextReady(false);
        setIsLoadingClasicos(false);
        setIsLoadingOpenAI(false);
        setAlert({
          show: true,
          message: error?.message || 'No pudimos seleccionar el particular activo.',
          type: 'error'
        });
      });

    return () => {
      cancelled = true;
    };
  }, [activeParticular, activeParticularId, isGeneralAdmin, isParticularContext]);

  const handleMenuNavigate = (item) => {
    setIsSidebarOpen(false);
    if (!item) return;

    if (isParticularContext) {
      if (isParticularChatGPTMenuItem(item) && !canUseParticularChatGPTFeatures) {
        setSubscriptionBlockedMessage('');
        setSubscriptionBlockedDismissed(false);
        setSubscriptionBlockedOpen(true);
        return;
      }

      if (isParticularCalendarioMenuItem(item) && !canUseParticularCalendarioFeature) {
        setSubscriptionBlockedMessage(CALENDARIO_SUBSCRIPTION_BLOCKED_MESSAGE);
        setSubscriptionBlockedDismissed(false);
        setSubscriptionBlockedOpen(true);
        return;
      }

      if (item.path) {
        navigate(item.path, item.state ? { state: item.state } : undefined);
        return;
      }

      navigate('/particular', { state: { tab: item.id } });
      return;
    }

    if (isAdmin) {
      if (item.path) {
        navigate(item.path, item.state ? { state: item.state } : undefined);
        return;
      }
      if (item.id === 'agentes-clasicos-gpt') {
        navigate('/mis-agentes-clasicos-gpt');
        return;
      }
      navigate('/admin-dashboard', { state: { tab: item.id } });
      return;
    }
    if (item.path) {
      navigate(item.path, item.state ? { state: item.state } : undefined);
    }
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const loadAsistentesClasicos = async (pagina = paginacionClasicos.paginaActual, tamañoPagina = paginacionClasicos.tamañoPagina) => {
    if (authLoading || !canAccessReports) return;
    if (isParticularContext && !particularAccessReady) return;
    if (isParticularContext && !canUseParticularChatGPTFeatures) {
      setIsLoadingClasicos(false);
      setAsistentesClasicos([]);
      return;
    }

    if (!hasAgentesClasicos) {
      setAsistentesClasicos([]);
      setPaginacionClasicos((prev) => ({ ...prev, total: 0, paginaActual: 1 }));
      setIsLoadingClasicos(false);
      return;
    }

    try {
      setIsLoadingClasicos(true);
      const { start, end } = getRange(pagina, tamañoPagina);

      const response = isParticularContext
        ? await particularesApiService.getAsistentes()
        : isAdmin
        ? await AdminAsistenteService.getAsistentes({ start, end, includeTotal: true, esClasico: true })
        : await AsistenteService.getMisAsistentesReportesClasicos({ start, end });

      const allItems = isParticularContext
        ? (response?.asistentes || response?.Asistentes || [])
        : isAdmin
          ? (response?.items || [])
          : (response?.asistentes || []);
      const items = isParticularContext ? allItems.slice(start, end + 1) : allItems;
      const normalizados = (items || []).map((asistente) => normalizeAssistant(asistente, { forceClasico: true }));

      setAsistentesClasicos(normalizados);
      setPaginacionClasicos((prev) => ({
        ...prev,
        paginaActual: pagina,
        tamañoPagina,
        total: isParticularContext ? allItems.length : response?.total ?? normalizados.length
      }));
    } catch (error) {
      console.error('❌ Reports - Error al cargar asistentes clásicos:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al cargar los asistentes IA.',
        type: 'error'
      });
      setAsistentesClasicos([]);
      setPaginacionClasicos((prev) => ({ ...prev, total: 0 }));
    } finally {
      setIsLoadingClasicos(false);
    }
  };

  const loadAsistentesOpenAI = async (pagina = paginacionOpenAI.paginaActual, tamañoPagina = paginacionOpenAI.tamañoPagina) => {
    if (authLoading || !canAccessReports) return;
    if (isParticularContext && !particularAccessReady) return;

    if (isParticularContext || !hasOpenAIChats) {
      setAsistentesOpenAI([]);
      setPaginacionOpenAI((prev) => ({ ...prev, total: 0, paginaActual: 1 }));
      setIsLoadingOpenAI(false);
      return;
    }

    try {
      setIsLoadingOpenAI(true);
      const { start, end } = getRange(pagina, tamañoPagina);

      const response = isAdmin
        ? await AdminAsistenteService.getAsistentes({ start, end, includeTotal: true, esClasico: false })
        : await AsistenteService.getMisAsistentes({
            start,
            end,
            esClasico: false,
            esAsignado: true
          });

      const items = isAdmin ? (response?.items || []) : (response?.asistentes || []);
      const normalizados = (items || []).map((asistente) => normalizeAssistant(asistente)).filter((asistente) => !asistente.esClasico);

      setAsistentesOpenAI(normalizados);
      setPaginacionOpenAI((prev) => ({
        ...prev,
        paginaActual: pagina,
        tamañoPagina,
        total: response?.total ?? normalizados.length
      }));
    } catch (error) {
      console.error('❌ Reports - Error al cargar asistentes OpenAI:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al cargar los asistentes OpenAI.',
        type: 'error'
      });
      setAsistentesOpenAI([]);
      setPaginacionOpenAI((prev) => ({ ...prev, total: 0 }));
    } finally {
      setIsLoadingOpenAI(false);
    }
  };

  const loadAllAsistentesClasicosForSearch = async () => {
    if (authLoading || !canAccessReports || !hasAgentesClasicos) return;
    if (isParticularContext && !particularAccessReady) return;

    try {
      setIsLoadingClasicosSearchPool(true);

      const response = isParticularContext
        ? await particularesApiService.getAsistentes()
        : isAdmin
        ? await AdminAsistenteService.getAsistentes({ includeTotal: true, esClasico: true })
        : await AsistenteService.getMisAsistentesReportesClasicos();

      const items = isParticularContext
        ? (response?.asistentes || response?.Asistentes || [])
        : isAdmin
          ? (response?.items || [])
          : (response?.asistentes || []);
      const normalizados = (items || []).map((asistente) => normalizeAssistant(asistente, { forceClasico: true }));

      setAsistentesClasicosSearchPool(normalizados);
      setClasicosSearchPoolLoaded(true);
    } catch (error) {
      console.error('❌ Reports - Error al cargar el pool completo de asistentes clásicos:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al preparar la búsqueda de asistentes IA.',
        type: 'error'
      });
      setAsistentesClasicosSearchPool(asistentesClasicos);
      setClasicosSearchPoolLoaded(true);
    } finally {
      setIsLoadingClasicosSearchPool(false);
    }
  };

  useEffect(() => {
    if (!isParticularSubscriptionBlocked) return;

    setAsistentesClasicos([]);
    setAsistentesOpenAI([]);
    setIsLoadingClasicos(false);
    setIsLoadingOpenAI(false);
    setSubscriptionBlockedDismissed(false);
    setSubscriptionBlockedOpen(true);
    setShowThreadsView(false);
    setSelectedAssistant(null);
  }, [isParticularSubscriptionBlocked]);

  useEffect(() => {
    if (authLoading) return;
    if (!canAccessReports) {
      setIsLoadingClasicos(false);
      setIsLoadingOpenAI(false);
      return;
    }
    if (isParticularContext && !particularAccessReady) return;
    if (isParticularContext && !canUseParticularChatGPTFeatures) return;

    if (hasAgentesClasicos) {
      loadAsistentesClasicos(paginacionClasicos.paginaActual, paginacionClasicos.tamañoPagina);
    } else {
      setAsistentesClasicos([]);
      setPaginacionClasicos((prev) => ({ ...prev, total: 0 }));
      setIsLoadingClasicos(false);
    }
  }, [authLoading, canAccessReports, hasAgentesClasicos, isAdmin, isParticularContext, particularAccessReady, paginacionClasicos.paginaActual, paginacionClasicos.tamañoPagina]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isClasicosSearchActive || clasicosSearchPoolLoaded || isLoadingClasicosSearchPool) return;
    if (isParticularContext && !particularAccessReady) return;
    if (isParticularContext && !canUseParticularChatGPTFeatures) return;
    loadAllAsistentesClasicosForSearch();
  }, [canUseParticularChatGPTFeatures, isClasicosSearchActive, clasicosSearchPoolLoaded, isLoadingClasicosSearchPool, isParticularContext, particularAccessReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authLoading) return;
    if (!canAccessReports) {
      setIsLoadingOpenAI(false);
      return;
    }
    if (isParticularContext && !particularAccessReady) return;

    if (!isParticularContext && hasOpenAIChats) {
      loadAsistentesOpenAI(paginacionOpenAI.paginaActual, paginacionOpenAI.tamañoPagina);
    } else {
      setAsistentesOpenAI([]);
      setPaginacionOpenAI((prev) => ({ ...prev, total: 0 }));
      setIsLoadingOpenAI(false);
    }
  }, [authLoading, canAccessReports, hasOpenAIChats, isAdmin, isParticularContext, particularAccessReady, paginacionOpenAI.paginaActual, paginacionOpenAI.tamañoPagina]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    if (!canAccessReports || authLoading) return;
    if (isParticularContext && !particularAccessReady) return;
    if (isParticularContext && !canUseParticularChatGPTFeatures) {
      setSubscriptionBlockedOpen(true);
      return;
    }
    if (showThreadsView && selectedAssistant) {
      loadMensajesAsistente(
        selectedAssistant,
        paginacionMensajes.paginaActual,
        paginacionMensajes.tamañoPagina,
        getCurrentMessagesFilters()
      );
      return;
    }
    if (hasAgentesClasicos) {
      loadAsistentesClasicos(paginacionClasicos.paginaActual, paginacionClasicos.tamañoPagina);
    }
    if (!isParticularContext && hasOpenAIChats) {
      loadAsistentesOpenAI(paginacionOpenAI.paginaActual, paginacionOpenAI.tamañoPagina);
    }
    if (hasAgentesClasicos && (isClasicosSearchActive || clasicosSearchPoolLoaded)) {
      loadAllAsistentesClasicosForSearch();
    }
  };

  const handleClasicosPageChange = (pagina) => {
    setPaginacionClasicos((prev) => ({ ...prev, paginaActual: pagina }));
  };

  const handleClasicosItemsPerPageChange = (tamañoPagina) => {
    setPaginacionClasicos((prev) => ({
      ...prev,
      paginaActual: 1,
      tamañoPagina
    }));
  };

  const handleOpenAIPageChange = (pagina) => {
    setPaginacionOpenAI((prev) => ({ ...prev, paginaActual: pagina }));
  };

  const handleOpenAIItemsPerPageChange = (tamañoPagina) => {
    setPaginacionOpenAI((prev) => ({
      ...prev,
      paginaActual: 1,
      tamañoPagina
    }));
  };

  const getCurrentMessagesFilters = () => ({
    texto: mensajesSearch.trim(),
    fechaDesde: mensajesDateRange.fechaDesde,
    fechaHasta: mensajesDateRange.fechaHasta
  });

  // Función para manejar la acción de ver mensajes
  const handleViewMessages = async (asistente) => {
    if (!canAccessReports) return;
    if (isAssistantPendingForReports(asistente)) return;

    setSelectedAssistant(asistente);
    setShowThreadsView(true);
    setConversationSource(isClasicoAssistant(asistente) ? 'gpt-clasico' : 'db');
    
    // Usar el ID de OpenAI del asistente
    const openAIId = asistente.idOpenAIAssistant;
    
    if (!openAIId) {
      setAlert({
        show: true,
        message: 'Error: No se encontró el ID de OpenAI para este asistente',
        type: 'error'
      });
      return;
    }
    
    const defaultDateRange = getDefaultMessagesDateRange();
    setMensajesSearch('');
    setMensajesDateRange(defaultDateRange);
    setPaginacionMensajes((prev) => ({
      ...prev,
      paginaActual: 1,
      tamañoPagina: DEFAULT_MESSAGES_PER_PAGE
    }));
    
    // Cargar los mensajes del asistente
    await loadMensajesAsistente(asistente, 1, DEFAULT_MESSAGES_PER_PAGE, {
      texto: '',
      ...defaultDateRange
    });
  };

  const renderMessagesAction = (item, title = 'Ver mensajes del asistente') => {
    if (isAssistantPendingForReports(item)) {
      return (
        <div className="table-actions">
          <span className="reports-pending-assistant-label">Asistente pendiente</span>
        </div>
      );
    }

    return (
      <div className="table-actions">
        <button
          className="table-btn table-btn-messages"
          title={title}
          onClick={() => handleViewMessages(item)}
        >
          <i className="fas fa-comments"></i>
        </button>
      </div>
    );
  };

  const flattenThreads = (threads = []) => {
    const mensajesAplanados = [];

    threads.forEach((thread) => {
      const mensajesThread = thread?.mensajes || thread?.Mensajes || [];
      const threadId = thread?.idThread || thread?.IdThread || '';
      const fechaCreacionThread = thread?.fechaCreacion || thread?.FechaCreacion || null;

      const mensajesOrdenados = mensajesThread
        .map((mensaje) => ({
          ...mensaje,
          rol: mensaje?.rol || mensaje?.Rol || '',
          contenido: mensaje?.contenido || mensaje?.Contenido || '',
          fechaEnvio: mensaje?.fechaEnvio || mensaje?.FechaEnvio || null,
        }))
        .sort((a, b) => {
          const aFecha = a.fechaEnvio ? new Date(a.fechaEnvio).getTime() : 0;
          const bFecha = b.fechaEnvio ? new Date(b.fechaEnvio).getTime() : 0;
          return aFecha - bFecha;
        });

      mensajesOrdenados.forEach((mensaje, index) => {
        let mensajeRecibido = '';

        if (mensaje.rol === 'user') {
          for (let i = index + 1; i < mensajesOrdenados.length; i += 1) {
            if (mensajesOrdenados[i].rol === 'assistant') {
              mensajeRecibido = mensajesOrdenados[i].contenido || '';
              break;
            }
          }
        }

        mensajesAplanados.push({
          ...mensaje,
          threadId,
          fechaCreacionThread: fechaCreacionThread ? new Date(fechaCreacionThread) : null,
          fechaEnviado: mensaje.fechaEnvio ? new Date(mensaje.fechaEnvio) : null,
          fecha: mensaje.fechaEnvio ? new Date(mensaje.fechaEnvio) : null,
          mensaje: mensaje.contenido || '',
          mensajeRecibido,
          usuario: mensaje.rol === 'user' ? 'Alumno' : 'Docente',
          rol: mensaje.rol
        });
      });
    });

    return mensajesAplanados;
  };

  const hydrateFromReport = (report) => {
    const threads = report?.threads || report?.Threads;
    const mensajesAplanados = Array.isArray(threads) && threads.length > 0
      ? flattenThreads(threads)
      : [];
    const totalMessages = report?.totalMessages || report?.TotalMessages || mensajesAplanados.length;
    const totalThreads = report?.totalThreads || report?.TotalThreads || (Array.isArray(threads) ? threads.length : 0);

    setEstadisticasMensajes({
      diaMasActivo: report?.diaMasActivo || report?.DiaMasActivo || null,
      mensajesDiaMasActivo: report?.mensajesDiaMasActivo ?? report?.MensajesDiaMasActivo ?? 0,
      horaPico: report?.horaPico ?? report?.HoraPico ?? null,
      mensajesHoraPico: report?.mensajesHoraPico ?? report?.MensajesHoraPico ?? 0,
      promedioMensajesPorDiaActivo: report?.promedioMensajesPorDiaActivo ?? report?.PromedioMensajesPorDiaActivo ?? 0,
      diasConActividad: report?.diasConActividad ?? report?.DiasConActividad ?? 0,
      ultimaActividad: report?.ultimaActividad || report?.UltimaActividad || null
    });

    setInformacionUtil({
      texto: report?.informacionUtilRangoFechas || report?.InformacionUtilRangoFechas || '',
      error: report?.informacionUtilError || report?.InformacionUtilError || '',
      mensajesAnalizados: report?.mensajesAnalizadosPorOpenAI ?? report?.MensajesAnalizadosPorOpenAI ?? 0
    });
    setMensajes(mensajesAplanados);
    setPaginacionMensajes({
      paginaActual: report?.paginaActual || report?.PaginaActual || 1,
      tamañoPagina: report?.tamañoPagina || report?.TamañoPagina || DEFAULT_MESSAGES_PER_PAGE,
      total: totalMessages,
      totalPaginas: report?.totalPaginas || report?.TotalPaginas || 1,
      totalThreads,
      totalThreadsEnBD: report?.totalThreadsEnBD || report?.TotalThreadsEnBD || totalThreads
    });
    setIsLoadingMensajes(false);
    return true;
  };

  // Función para cargar los mensajes del asistente
  const loadMensajesAsistente = async (
    assistantOrId,
    pagina = 1,
    tamañoPagina = paginacionMensajes.tamañoPagina,
    filtersOverride = null
  ) => {
    if (!canAccessReports) {
      setIsLoadingMensajes(false);
      return;
    }

    const assistant = typeof assistantOrId === 'string'
      ? (selectedAssistant || { idOpenAIAssistant: assistantOrId })
      : assistantOrId;
    const asistenteId = typeof assistantOrId === 'string'
      ? assistantOrId
      : (assistantOrId?.idOpenAIAssistant || assistantOrId?.IdOpenAIAssistant || '');

    if (!asistenteId) {
      setIsLoadingMensajes(false);
      return;
    }

    setIsLoadingMensajes(true);
    const filters = filtersOverride || getCurrentMessagesFilters();
    try {
      const response = isClasicoAssistant(assistant)
        ? await GptClasicoReportsService.getReportes(asistenteId, {
            pagina,
            tamañoPagina,
            limiteMensajesPorThread: 200,
            filtroRol: 'user',
            ...filters
          })
        : await ThreadsService.getThreadsMessagesFromDb(
            asistenteId,
            'user',
            pagina,
            tamañoPagina,
            200,
            filters
          );

      hydrateFromReport(response);
      
    } catch (error) {
      console.error('Error cargando mensajes:', error);
      const errorMessage = error?.message || 'Error al cargar los mensajes del asistente';
      setAlert({
        show: true,
        message: errorMessage === 'Error al cargar los mensajes del asistente'
          ? errorMessage
          : `Error al cargar los mensajes del asistente: ${errorMessage}`,
        type: 'error'
      });
      setMensajes([]);
      setEstadisticasMensajes(EMPTY_MENSAJES_STATS);
      setInformacionUtil({ texto: '', error: '', mensajesAnalizados: 0 });
      setPaginacionMensajes(prev => ({
        ...prev,
        total: 0,
        totalPaginas: 0
      }));
    } finally {
      setIsLoadingMensajes(false);
    }
  };

  // Función para volver a la vista de asistentes
  const handleBackToAssistants = () => {
    const defaultDateRange = getDefaultMessagesDateRange();
    setShowThreadsView(false);
    setSelectedAssistant(null);
    setMensajes([]);
    setMensajesSearch('');
    setMensajesDateRange(defaultDateRange);
    setInformacionUtil({ texto: '', error: '', mensajesAnalizados: 0 });
    setEstadisticasMensajes(EMPTY_MENSAJES_STATS);
    setPaginacionMensajes({
      paginaActual: 1,
      tamañoPagina: DEFAULT_MESSAGES_PER_PAGE,
      total: 0,
      totalPaginas: 0
    });
  };

  useEffect(() => {
    if (!canAccessReports || !location?.state) return;
    const assistantFromState = location.state.assistant || null;
    const assistantId = assistantFromState?.idOpenAIAssistant
      || assistantFromState?.IdOpenAIAssistant
      || location.state.assistantId;
    if (!assistantId) return;
    const assistantName = assistantFromState?.nombreAsistente
      || assistantFromState?.nombre
      || assistantFromState?.Nombre
      || location.state.assistantName
      || 'Asistente';
    const assistantPayload = assistantFromState || { idOpenAIAssistant: assistantId, nombreAsistente: assistantName };
    const assistantIsClassic = isClasicoAssistant(assistantPayload);
    if ((assistantIsClassic && !hasAgentesClasicos) || (!assistantIsClassic && !hasOpenAIChats)) {
      return;
    }
    setSelectedAssistant(assistantPayload);
    setShowThreadsView(true);
    const preloaded = location.state.preloadReport;
    if (!preloaded || !hydrateFromReport(preloaded)) {
      loadMensajesAsistente(assistantPayload);
    }
  }, [canAccessReports, hasAgentesClasicos, hasOpenAIChats, location]);

  useEffect(() => {
    const defaultDateRange = getDefaultMessagesDateRange();
    setMensajesSearch('');
    setMensajesDateRange(defaultDateRange);
  }, [selectedAssistant?.idOpenAIAssistant]);

  const filteredAsistentesClasicos = useMemo(() => {
    const source = isClasicosSearchActive ? asistentesClasicosSearchPool : asistentesClasicos;
    if (!isClasicosSearchActive) {
      return source;
    }

    return source.filter((asistente) => {
      const searchableValues = [
        asistente?.nombreAsistente,
        asistente?.nombre,
        asistente?.Nombre,
        asistente?.nombreArea,
        asistente?.NombreArea,
        asistente?.nombreMateria,
        asistente?.NombreMateria,
        getClasicosSearchableDate(asistente?.fechaAlta),
        getClasicosSearchableDate(asistente?.FechaAlta)
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());

      return searchableValues.some((value) => value.includes(normalizedClasicosSearch));
    });
  }, [asistentesClasicos, asistentesClasicosSearchPool, isClasicosSearchActive, normalizedClasicosSearch]);

  const clasicosTableLoading = isLoadingClasicos || (isClasicosSearchActive && isLoadingClasicosSearchPool && !clasicosSearchPoolLoaded);

  if (authLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <LoadingSpinner message="Verificando permisos..." />
        </div>
      </div>
    );
  }

  if (!canAccessReports) {
    return (
      <div className="dashboard-container">
        <div
          style={{
            maxWidth: '640px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.1)',
            textAlign: 'center'
          }}
        >
          <h2>Acceso restringido</h2>
          <p>Los reportes están disponibles únicamente para usuarios con rol Profesor o Admin.</p>
        </div>
      </div>
    );
  }

  // Función para manejar cambio de página en mensajes
  const handlePageChange = (nuevaPagina) => {
    if (selectedAssistant && nuevaPagina !== paginacionMensajes.paginaActual) {
      // Usar el ID de OpenAI del asistente
      const openAIId = selectedAssistant.idOpenAIAssistant;
      if (openAIId) {
        loadMensajesAsistente(selectedAssistant, nuevaPagina, paginacionMensajes.tamañoPagina, getCurrentMessagesFilters());
      }
    }
  };

  const handleItemsPerPageChange = (nuevoTamañoPagina) => {
    if (!selectedAssistant) return;

    setPaginacionMensajes((prev) => ({
      ...prev,
      paginaActual: 1,
      tamañoPagina: nuevoTamañoPagina
    }));
    loadMensajesAsistente(selectedAssistant, 1, nuevoTamañoPagina, getCurrentMessagesFilters());
  };

  const handleMensajesFilterSubmit = (event) => {
    event.preventDefault();
    if (!selectedAssistant) return;
    loadMensajesAsistente(selectedAssistant, 1, paginacionMensajes.tamañoPagina, getCurrentMessagesFilters());
  };

  const handleMensajesDateChange = (event) => {
    const { name, value } = event.target;
    setMensajesDateRange((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClearMensajesSearch = () => {
    if (!selectedAssistant) {
      setMensajesSearch('');
      return;
    }

    const nextFilters = {
      ...getCurrentMessagesFilters(),
      texto: ''
    };
    setMensajesSearch('');
    loadMensajesAsistente(selectedAssistant, 1, paginacionMensajes.tamañoPagina, nextFilters);
  };

  const isOpenAIHistoryEnabled = () => {
    if (isClasicoAssistant(selectedAssistant)) {
      return false;
    }

    const assistantFlags = selectedAssistant?.featureFlags || selectedAssistant?.FeatureFlags || [];
    const colegioFlags = ColegioService.getFeatureFlags();
    const flags = assistantFlags.length > 0 ? assistantFlags : colegioFlags;
    return flags.some(flag => {
      const nombre = flag?.nombre || flag?.Nombre;
      const activo = flag?.activo ?? flag?.Activo;
      return nombre === 'PermiteObtenerHistorialDesdeOpenAI' && Boolean(activo);
    });
  };

  // Función para manejar la visualización de conversación completa
  const handleViewConversation = (message, source = 'db') => {
    setSelectedMessage(message);
    setConversationSource(source);
    setShowConversationModal(true);
  };  // Función para cerrar el modal de conversación
  const handleCloseConversationModal = () => {
    setShowConversationModal(false);
    setSelectedMessage(null);
  };

  // Función para remover etiquetas HTML y mostrar solo texto plano
  const stripHtmlTags = (html) => {
    if (!html) return '';
    
    // Crear un elemento temporal para usar la API del navegador
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Obtener solo el texto sin etiquetas
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // Limpiar espacios excesivos y saltos de línea
    return textContent.replace(/\s+/g, ' ').trim();
  };

  const handleBackToList = () => {
    const defaultDateRange = getDefaultMessagesDateRange();
    setShowThreadsView(false);
    setSelectedAssistant(null);
    setMensajes([]);
    setMensajesSearch('');
    setMensajesDateRange(defaultDateRange);
    setInformacionUtil({ texto: '', error: '', mensajesAnalizados: 0 });
    setEstadisticasMensajes(EMPTY_MENSAJES_STATS);
    setPaginacionMensajes({
      paginaActual: 1,
      tamañoPagina: DEFAULT_MESSAGES_PER_PAGE,
      total: 0,
      totalPaginas: 0
    });
  };

  // Formatear fecha para mostrar
  const formatFecha = (fecha) => {
    if (!fecha) return '-';

    const fechaObj = fecha instanceof Date ? fecha : new Date(fecha);
    if (Number.isNaN(fechaObj.getTime())) {
      return '-';
    }

    return fechaObj.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFechaCorta = (fecha) => {
    if (!fecha) return 'Sin datos';

    const fechaObj = fecha instanceof Date ? fecha : new Date(fecha);
    if (Number.isNaN(fechaObj.getTime())) {
      return 'Sin datos';
    }

    return fechaObj.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatFechaArgentina = (fecha) => {
    if (!fecha) return 'N/A';

    const fechaObj = fecha instanceof Date ? fecha : new Date(fecha);
    if (Number.isNaN(fechaObj.getTime())) {
      return 'N/A';
    }

    return fechaObj.toLocaleString('es-AR', {
      timeZone: ARGENTINA_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatHoraPico = (hora) => {
    if (hora === null || hora === undefined || Number.isNaN(Number(hora))) {
      return 'Sin datos';
    }

    const hourNumber = Number(hora);
    const argentinaHour = ((hourNumber + ARGENTINA_UTC_OFFSET_HOURS) % 24 + 24) % 24;
    return `${String(argentinaHour).padStart(2, '0')}:00`;
  };

  // Configuración de columnas para la tabla
  const columns = [
    {
      key: 'nombreAsistente',
      title: 'Nombre del Asistente',
      sortable: true,
      className: 'font-weight-bold'
    },
    {
      key: 'nombreArea',
      title: 'Área',
      sortable: true
    },
    {
      key: 'nombreMateria',
      title: 'Materia',
      sortable: true
    },
    {
      key: 'fechaAlta',
      title: 'Fecha de Creación',
      sortable: true,
      align: 'center',
      width: 'lg',
      className: 'date-cell',
      formatter: formatFecha
    },
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      align: 'center',
      width: 'md',
      render: (value, item) => renderMessagesAction(item, 'Ver mensajes del asistente')
    }
  ];

  const particularColumns = [
    {
      key: 'nombreAsistente',
      title: 'GPT',
      sortable: true,
      className: 'font-weight-bold'
    },
    {
      key: 'descripcion',
      title: 'Descripción',
      sortable: true
    },
    {
      key: 'estado',
      title: 'Estado',
      sortable: true,
      align: 'center'
    },
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      align: 'center',
      width: 'md',
      render: (value, item) => renderMessagesAction(item, 'Ver mensajes del GPT')
    }
  ];

  const assistantColumns = isParticularContext ? particularColumns : columns;

  // Definir las columnas para la tabla de mensajes
  const columnasMensajes = [
    {
      key: 'fechaEnviado',
      title: 'Fecha de Envío',
      sortable: true,
      align: 'center',
      width: 'md',
      className: 'date-cell',
      formatter: formatFechaArgentina
    },
    {
      key: 'usuario',
      title: 'Tipo de Usuario',
      sortable: true,
      className: 'font-weight-bold user-type-cell',
      render: (usuario, item) => {
        const rol = item.rol || 'unknown';
        if (rol === 'user') {
          return (
            <span className="user-badge student">
              <i className="fas fa-user-graduate"></i>
              Alumno
            </span>
          );
        } else if (rol === 'assistant') {
          return (
            <span className="user-badge assistant">
              <i className="fas fa-robot"></i>
              Docente
            </span>
          );
        }
        return usuario || 'Usuario';
      }
    },
    {
      key: 'mensaje',
      title: 'Contenido del Mensaje',
      sortable: false,
      className: 'message-cell',
      formatter: (mensaje) => {
        // Limpiar HTML del mensaje y truncar si es necesario
        const mensajeTexto = stripHtmlTags(mensaje || '');
        return mensajeTexto.length > 200 
          ? `${mensajeTexto.substring(0, 200)}...` 
          : mensajeTexto;
      }
    },
    {
      key: 'mensajeRecibido',
      title: 'Respuesta del Asistente',
      sortable: false,
      className: 'message-cell',
      render: (mensajeRecibido, item) => {
        if (!mensajeRecibido || item?.rol === 'assistant') {
          return '—';
        }
        const mensajeTexto = stripHtmlTags(mensajeRecibido || '');
        return mensajeTexto.length > 200 
          ? `${mensajeTexto.substring(0, 200)}...` 
          : mensajeTexto;
      }
    },
    {
      key: 'threadId',
      title: 'Thread ID',
      sortable: true,
      align: 'center',
      width: 'sm',
      className: 'thread-id-cell',
      formatter: (threadId) => {
        if (!threadId) return 'N/A';
        // Mostrar solo los últimos 8 caracteres para que sea más legible
        return threadId.length > 8 ? `...${threadId.slice(-8)}` : threadId;
      }
    },
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      align: 'center',
      width: 'sm',
      className: 'actions-cell',
      render: (value, item) => (
        <div className="table-actions">
          <button
            className="table-btn table-btn-view"
            title="Ver conversación completa (BD)"
            onClick={() => handleViewConversation(item, 'db')}
          >
            <i className="fas fa-database"></i>
          </button>
          {isOpenAIHistoryEnabled() && (
            <button
              className="table-btn table-btn-view"
              title="Ver conversación completa (OpenAI)"
              onClick={() => handleViewConversation(item, 'openai')}
            >
              <i className="fas fa-cloud"></i>
            </button>
          )}
        </div>
      )
    }
  ];

  const columnasMensajesVisibles = isClasicoAssistant(selectedAssistant)
    ? columnasMensajes.filter(
        (column) => !['mensajeRecibido', 'threadId', 'actions'].includes(column.key)
      )
    : columnasMensajes;

  const filteredMensajes = mensajes;
  const informacionUtilError = typeof informacionUtil.error === 'string'
    ? informacionUtil.error.trim()
    : '';
  const shouldShowInformacionUtil = isLoadingMensajes || !informacionUtilError;

  const summaryCardsMensajes = [
    {
      label: 'Total Mensajes',
      value: paginacionMensajes.total || 0,
      detail: `${paginacionMensajes.totalThreads || new Set(mensajes.map((mensaje) => mensaje.threadId)).size} conversaciones registradas`
    },
    {
      label: 'Día Más Concurrido',
      value: formatFechaCorta(estadisticasMensajes.diaMasActivo),
      detail: estadisticasMensajes.mensajesDiaMasActivo > 0
        ? `${estadisticasMensajes.mensajesDiaMasActivo} mensajes ese día`
        : 'Sin actividad registrada'
    },
    {
      label: 'Pico De Mensajes',
      value: estadisticasMensajes.mensajesDiaMasActivo || 0,
      detail: estadisticasMensajes.diaMasActivo
        ? `Máximo en un día: ${formatFechaCorta(estadisticasMensajes.diaMasActivo)}`
        : 'Sin actividad registrada'
    },
    {
      label: 'Hora Pico',
      value: formatHoraPico(estadisticasMensajes.horaPico),
      detail: estadisticasMensajes.mensajesHoraPico > 0
        ? `${estadisticasMensajes.mensajesHoraPico} mensajes en esa franja`
        : 'Sin actividad registrada'
    }
  ];

  if (showThreadsView && selectedAssistant) {
    return (
      <div className={`new-dashboard reports ${isSidebarOpen ? 'sidebar-open' : ''}`}>
        {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
        {/* Sidebar */}
        <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <i className="fas fa-graduation-cap"></i>
              <span>aluxIA</span>
            </div>
          </div>
          
          {isParticularContext ? (
            <ParticularDashboardMenu
              activeId="reportes"
              onNavigate={handleMenuNavigate}
              externalNavigation
              messageScope="particular"
            />
          ) : isAdmin ? (
            <AdminMenu
              activeId="reportes"
              user={user}
              featureFlags={ColegioService.getFeatureFlags()}
              onNavigate={handleMenuNavigate}
            />
          ) : (
            <ProfessorMenu
              activeId="reportes"
              hasOpenAIChats={hasOpenAIChats}
              hasAgentesClasicos={hasAgentesClasicos}
              hasModuloAlumnos={hasModuloAlumnos}
              onNavigate={handleMenuNavigate}
            />
          )}
          
          <div className="sidebar-footer">
            <div className="sidebar-user">
              <div className="user-avatar">
                <i className={sidebarUserIcon}></i>
              </div>
              <div className="user-info">
                <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
                <span className="user-role">{sidebarRoleLabel}</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content - Threads View */}
        <main className="dashboard-main">
          <header className="dashboard-header">
            <div className="header-left">
              <button
                type="button"
                className="sidebar-toggle"
                onClick={() => setIsSidebarOpen((prev) => !prev)}
                title="Mostrar menú"
              >
                <i className="fas fa-bars"></i>
              </button>
              <button 
                className="btn btn-back"
                onClick={handleBackToList}
                title="Volver a la lista"
              >
                <i className="fas fa-arrow-left"></i>
                Volver
              </button>
              <div className="header-info">
                <h1 className="page-title">
                  <i className="fas fa-comments"></i>
                  Mensajes: {selectedAssistant.nombreAsistente}
                </h1>
                <p className="page-subtitle">Historial completo de conversaciones con estudiantes</p>
              </div>
            </div>
            
          <div className="header-right">
            <SolicitudesDropdown />
            {!isParticularContext && <ActiveColegioBadge />}
            <button 
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Actualizar datos"
              >
                <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
                Actualizar
              </button>
              
              <button className="logout-btn-header" onClick={handleLogout} title="Cerrar Sesión">
                <i className="fas fa-sign-out-alt"></i>
                <span className="logout-text">Salir</span>
              </button>
            </div>
          </header>

          <div className="dashboard-content">
            {/* Alert */}
            {alert.show && (
              <Alert
                title={alert.title}
                message={alert.message}
                type={alert.type}
                onClose={closeAlert}
              />
            )}

            {/* Mensajes Content */}
            <div className="content-card">
              <div className="card-header">
                <div className="card-header-left">
                  <h2 className="card-title">
                    <i className="fas fa-comments"></i>
                    Mensajes de Estudiantes
                  </h2>
                  <p className="card-subtitle">
                    Mensajes enviados por estudiantes al asistente <strong>{selectedAssistant.nombreAsistente}</strong>
                  </p>
                </div>
                <div className="card-header-right">
                  <button 
                    className="btn btn-outline-secondary"
                    onClick={handleBackToAssistants}
                    title="Volver a la lista de asistentes"
                  >
                    <i className="fas fa-arrow-left"></i>
                    Volver
                  </button>
                </div>
              </div>
              
              {/* Estadísticas de mensajes */}
              {!isLoadingMensajes && (paginacionMensajes.total > 0 || mensajes.length > 0) && (
                <div className="messages-stats">
                  {summaryCardsMensajes.map((card) => (
                    <div key={card.label} className="stat-item">
                      <span className="stat-number">{card.value}</span>
                      <span className="stat-label">{card.label}</span>
                      <span className="stat-detail">{card.detail}</span>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="card-content">
                <form className="reports-messages-toolbar" onSubmit={handleMensajesFilterSubmit}>
                  <div className="reports-search">
                    <i className="fas fa-search" aria-hidden="true" />
                    <input
                      type="text"
                      placeholder="Buscar en mensajes de alumnos..."
                      value={mensajesSearch}
                      onChange={(e) => setMensajesSearch(e.target.value)}
                    />
                    {mensajesSearch && (
                      <button
                        type="button"
                        className="reports-search-clear"
                        onClick={handleClearMensajesSearch}
                        title="Limpiar búsqueda"
                      >
                        <i className="fas fa-times" />
                      </button>
                    )}
                  </div>
                  <label className="reports-date-filter">
                    <span>Desde</span>
                    <input
                      type="date"
                      name="fechaDesde"
                      value={mensajesDateRange.fechaDesde}
                      onChange={handleMensajesDateChange}
                    />
                  </label>
                  <label className="reports-date-filter">
                    <span>Hasta</span>
                    <input
                      type="date"
                      name="fechaHasta"
                      value={mensajesDateRange.fechaHasta}
                      onChange={handleMensajesDateChange}
                    />
                  </label>
                  <button className="btn btn-secondary reports-filter-btn" type="submit" disabled={isLoadingMensajes}>
                    <i className={`fas ${isLoadingMensajes ? 'fa-spinner fa-spin' : 'fa-filter'}`} />
                    Filtrar
                  </button>
                </form>

                {shouldShowInformacionUtil && (
                  <section className="reports-useful-info">
                    <div className="reports-useful-info-header">
                      <i className="fas fa-lightbulb" />
                      <div>
                        <h3>Información útil para ese rango de fechas</h3>
                        <p>{informacionUtil.mensajesAnalizados || 0} mensajes enviados al asistente de análisis</p>
                      </div>
                    </div>
                    {isLoadingMensajes ? (
                      <div className="reports-useful-info-loading">
                        <i className="fas fa-spinner fa-spin" />
                        Generando información útil...
                      </div>
                    ) : (
                      <div className="reports-useful-info-body">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {informacionUtil.texto || 'No hay información útil disponible para el rango seleccionado.'}
                        </ReactMarkdown>
                      </div>
                    )}
                  </section>
                )}

                <DataTable
                  data={filteredMensajes}
                  columns={columnasMensajesVisibles}
                  loading={isLoadingMensajes}
                  loadingMessage="Cargando mensajes..."
                  emptyMessage={mensajesSearch || mensajesDateRange.fechaDesde || mensajesDateRange.fechaHasta
                    ? 'No se encontraron mensajes que coincidan con la búsqueda.'
                    : 'Este asistente aún no ha recibido mensajes de estudiantes.'}
                  className="reports-table"
                  itemsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
                  externalPagination={true}
                  externalTotalItems={paginacionMensajes.total}
                  externalTotalPages={paginacionMensajes.totalPaginas}
                  externalCurrentPage={paginacionMensajes.paginaActual}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  defaultItemsPerPage={paginacionMensajes.tamañoPagina}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Modal para ver conversación completa */}
        {showConversationModal && selectedMessage && (
          <ConversationModal
            isOpen={showConversationModal}
            onClose={handleCloseConversationModal}
            threadId={selectedMessage.threadId}
            assistantName={selectedAssistant?.nombreAsistente || 'Asistente OpenAI'}
            assistantId={selectedAssistant?.idOpenAIAssistant || 'asst_placeholder'}
            useOpenAI={conversationSource === 'openai'}
            colegioId={selectedAssistant?.idColegio || ColegioService.getActiveColegioId()}
          />
        )}
        <SubscriptionBlockedModal
          open={subscriptionBlockedModalOpen}
          message={subscriptionBlockedMessage || undefined}
          onClose={closeSubscriptionBlockedModal}
        />
      </div>
    );
  }

  return (
    <div className={`new-dashboard reports ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>aluxIA</span>
          </div>
        </div>
        
        {isParticularContext ? (
          <ParticularDashboardMenu
            activeId="reportes"
            onNavigate={handleMenuNavigate}
            externalNavigation
            messageScope="particular"
          />
        ) : isAdmin ? (
          <AdminMenu
            activeId="reportes"
            user={user}
            featureFlags={ColegioService.getFeatureFlags()}
            onNavigate={handleMenuNavigate}
          />
        ) : (
          <ProfessorMenu
            activeId="reportes"
            hasOpenAIChats={hasOpenAIChats}
            hasAgentesClasicos={hasAgentesClasicos}
            hasModuloAlumnos={hasModuloAlumnos}
            onNavigate={handleMenuNavigate}
          />
        )}
        
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className={sidebarUserIcon}></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{sidebarRoleLabel}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Mostrar menú"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">
              <i className="fas fa-chart-bar"></i>
              Reportes de asistentes
            </h1>
            <p className="page-subtitle">
              {isParticularContext
                ? 'Analiza el uso de los asistentes IA creados para este particular'
                : 'Analiza el uso de tus asistentes IA y OpenAI creados'}
            </p>
          </div>
          
          <div className="header-right">
            {!isParticularContext && <ActiveColegioBadge />}
            <button 
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Actualizar lista"
            >
              <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
              Actualizar
            </button>
            
            <UserSessionMenu
              user={user}
              onLogout={handleLogout}
              extraItems={isParticularContext && isGeneralAdmin ? [{
                key: 'switch-particular',
                label: 'Cambiar particular',
                icon: 'fas fa-exchange-alt',
                onClick: handleChangeParticular
              }] : []}
            />
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          {isParticularContextPending ? (
            <div className="content-card">
              <div className="card-content">
                <LoadingSpinner message="Verificando suscripción..." size="medium" inline />
              </div>
            </div>
          ) : null}

          {!isParticularContextPending && !isParticularSubscriptionBlocked && !isParticularContext && !hasAgentesClasicos && !hasOpenAIChats ? (
            <div className="content-card">
              <div className="card-content">
                <div className="empty-state">
                  <div className="empty-state-content">
                    <i className="fas fa-ban empty-state-icon"></i>
                    <h3>Reportes sin asistentes habilitados</h3>
                    <p>Este colegio no tiene habilitados asistentes OpenAI ni asistentes IA.</p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {!isParticularContextPending && !isParticularSubscriptionBlocked && hasAgentesClasicos && (
            <div className="content-card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header">
                <h2 className="card-title">
                  <i className="fas fa-robot"></i>
                  Asistentes IA para reportes
                </h2>
              </div>
              
              <div className="card-content">
                <div className="reports-table-toolbar">
                  <div className="reports-search">
                    <i className="fas fa-search"></i>
                    <input
                      type="text"
                      value={clasicosSearch}
                      onChange={(event) => setClasicosSearch(event.target.value)}
                      placeholder="Filtrar asistentes por nombre, área, materia o fecha"
                    />
                    {clasicosSearch && (
                      <button
                        type="button"
                        className="reports-search-clear"
                        onClick={() => setClasicosSearch('')}
                        aria-label="Limpiar búsqueda"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>
                <DataTable
                  data={filteredAsistentesClasicos}
                  columns={assistantColumns}
                  loading={clasicosTableLoading}
                  loadingMessage="Obteniendo asistentes IA..."
                  emptyMessage={isClasicosSearchActive
                    ? 'No encontramos asistentes IA que coincidan con esa búsqueda.'
                    : 'No hay asistentes IA disponibles para generar reportes.'}
                  className="reports-table"
                  externalPagination={!isClasicosSearchActive}
                  externalTotalItems={paginacionClasicos.total}
                  externalTotalPages={Math.max(1, Math.ceil(paginacionClasicos.total / paginacionClasicos.tamañoPagina))}
                  externalCurrentPage={paginacionClasicos.paginaActual}
                  onPageChange={handleClasicosPageChange}
                  onItemsPerPageChange={handleClasicosItemsPerPageChange}
                  defaultItemsPerPage={paginacionClasicos.tamañoPagina}
                />
              </div>
            </div>
          )}

          {!isParticularContextPending && !isParticularSubscriptionBlocked && !isParticularContext && hasOpenAIChats && (
            <div className="content-card">
              <div className="card-header">
                <h2 className="card-title">
                  <i className="fas fa-robot"></i>
                  Asistentes OpenAI para reportes
                </h2>
              </div>

              <div className="card-content">
                <DataTable
                  data={asistentesOpenAI}
                  columns={columns}
                  loading={isLoadingOpenAI}
                  loadingMessage="Obteniendo asistentes OpenAI..."
                  emptyMessage="No hay asistentes OpenAI disponibles para generar reportes."
                  className="reports-table"
                  externalPagination={true}
                  externalTotalItems={paginacionOpenAI.total}
                  externalTotalPages={Math.max(1, Math.ceil(paginacionOpenAI.total / paginacionOpenAI.tamañoPagina))}
                  externalCurrentPage={paginacionOpenAI.paginaActual}
                  onPageChange={handleOpenAIPageChange}
                  onItemsPerPageChange={handleOpenAIItemsPerPageChange}
                  defaultItemsPerPage={paginacionOpenAI.tamañoPagina}
                />
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modal para ver conversación completa */}
      {showConversationModal && selectedMessage && (
        <ConversationModal
          isOpen={showConversationModal}
          onClose={handleCloseConversationModal}
          threadId={selectedMessage.threadId}
          assistantName={selectedAssistant?.nombreAsistente || 'Asistente OpenAI'}
          assistantId={selectedAssistant?.idOpenAIAssistant || 'asst_placeholder'}
          useOpenAI={conversationSource === 'openai'}
          colegioId={selectedAssistant?.idColegio || ColegioService.getActiveColegioId()}
        />
      )}
      <SubscriptionBlockedModal
        open={subscriptionBlockedModalOpen}
        message={subscriptionBlockedMessage || undefined}
        onClose={closeSubscriptionBlockedModal}
      />
    </div>
  );
};

export default Reports;
